import { K, isEmpty, isTrueStringMatch } from "../utils";

// defaultMethod :: Any -> true
const defaultMethod = K(true);

// lowerCaseBinaryFn :: Function -> Function(Any, Any) -> Boolean
export const lowerCaseBinaryFn = fn => (a, b) => fn(String(a).toLowerCase(), String(b).toLowerCase());

// conditionMethods :: Keyed Object of binary functions, each of the form:
// (thisFieldsValue, otherFieldsValue) -> Boolean
export const conditionMethods = {
  0: lowerCaseBinaryFn((a, b) => `${a}` === `${b}`),
  // ==
  1: lowerCaseBinaryFn((a, b) => `${a}` !== `${b}`),
  // !=
  2: (a, b) => parseFloat(a) < parseFloat(b),
  // <
  3: (a, b) => parseFloat(a) <= parseFloat(b),
  // <=
  4: (a, b) => parseFloat(a) > parseFloat(b),
  // >
  5: (a, b) => parseFloat(a) >= parseFloat(b),
  // >=
  6: lowerCaseBinaryFn((a, b) => `${a}`.includes(`${b}`)),
  // string contains...
  7: lowerCaseBinaryFn((a, b) => `${a}`.startsWith(`${b}`)),
  // string starts with...
  8: lowerCaseBinaryFn((a, b) => `${a}`.endsWith(`${b}`)),
  // string ends with...
  9: lowerCaseBinaryFn((a, b) => !`${a}`.includes(`${b}`)),
  // string doesn't contain...
  10: lowerCaseBinaryFn((a, b) => !`${a}`.startsWith(`${b}`)),
  // string doesn't start with...
  11: lowerCaseBinaryFn((a, b) => !`${a}`.endsWith(`${b}`)) // string doesn't end with...
};

// areAllAndConditionsValid :: Array -> Boolean
// can break early on the first false case
export const areAllAndConditionsValid = conditions => {
  for (const {
    operator,
    valid
  } of conditions) {
    if (operator !== 1 && !valid) {
      return false;
    }
  }
  return true;
};
export const getFieldValue = (entryId, values, rowGuid) => {
  let value;
  // Check if there's an entry that matches
  const {
    value: entryFieldValue
  } = values.find(({
    guid
  }) => guid === entryId) || {};
  if (entryFieldValue) {
    value = entryFieldValue;
  } else if (rowGuid) {
    // No match, check loop entries for matching field
    const {
      value: loopEntryFieldValue
    } = values.find(({
      parentGuid,
      rowGuid: entryRowGuid
    }) => parentGuid === entryId && entryRowGuid === rowGuid) || {};
    if (loopEntryFieldValue) {
      value = loopEntryFieldValue;
    }
  }
  return value;
};

// evaluateCondition :: (Object, Array) -> Object
export const evaluateCondition = (condition, values, filterByRowGuid = false) => {
  if (!condition) {
    return true;
  }
  const {
    original_type_id,
    entry,
    value,
    operator
  } = condition;
  if (original_type_id === undefined || isEmpty(value)) {
    return true;
  }
  const conditionMethod = conditionMethods[original_type_id] || defaultMethod;
  const fieldValue = getFieldValue(entry, values, filterByRowGuid);
  return {
    valid: conditionMethod(fieldValue, value),
    operator
  };
};

// checkCombinedConditions :: (Array) -> Boolean
export const checkCombinedConditions = conditions => {
  const validOrCondition = conditions.find(({
    operator,
    valid
  }) => valid && operator === 1);
  if (validOrCondition) {
    return true;
  }
  return areAllAndConditionsValid(conditions);
};

// given a sheet with an specific id and an array of conditions and form values, filter out field conditions and determine if a given sheet is valid/allowed in the final output
// conditionalScreenFilter :: (Object, Array, Array) -> Boolean
export const conditionalScreenFilter = ({
  id
}, conditions, previewValues) => {
  const screenConditions = conditions.filter(({
    parentEntryGuid,
    sheet_id = ''
  }) => {
    return !parentEntryGuid && isTrueStringMatch(sheet_id, id);
  });
  const conditionStates = screenConditions.map(condition => evaluateCondition(condition, previewValues));
  return checkCombinedConditions(conditionStates);
};

// TODO: optimize for loops, where we only need to consider fields/values from active rows & values from fields external to rows
// given an entryID and an array of field values and an array of conditions, determine if this
// field is valid/allowed in the final output
// conditionCheck :: (String, Array, Array) -> Boolean
export const conditionCheck = (entryId, entries, conditions, filterByRowGuid = false) => {
  // only consider conditions that relate to the field
  const entryConditions = conditions.filter(({
    parentEntryGuid = ''
  }) => {
    return parentEntryGuid === entryId;
  });
  const conditionStates = entryConditions.map(condition => evaluateCondition(condition, entries, filterByRowGuid));
  return checkCombinedConditions(conditionStates);
};